import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import WebPreview from './WebPreview';
import { claveImpresion, sinComillas } from '../data/impresion';
import '../ds/tokens/fonts.css';
import '../ds/tokens/tokens.css';
import '../ds/styles/components.css';
import '../guia-home.css';
import './Dashboard.css';
import Brujula from '../ds/components/Brujula';
import Button from '../ds/components/Button';
import FondoMosaico from './FondoMosaico';
const SECTIONS = ['negocio', 'identidad', 'servicios', 'contacto'];

const SECTION_LABELS = {
  negocio:   { icon: 'negocio',   label: 'Mi negocio' },
  identidad: { icon: 'identidad', label: 'Identidad visual' },
  servicios: { icon: 'servicios', label: 'Servicios' },
  contacto:  { icon: 'contacto',  label: 'Contacto' },
};

const DEMO = {
  nombre:      'Estudio Raíz',
  rubro:       'Psicología y bienestar',
  tagline:     'Un espacio para volver a vos.',
  descripcion: 'Acompañamos procesos terapéuticos individuales y de pareja, con un enfoque humano, cercano y orientado al resultado.',
  servicios:   [
    { title: 'Terapia individual',    desc: 'Sesiones presenciales y online adaptadas a cada proceso.' },
    { title: 'Terapia de pareja',     desc: 'Acompañamiento para mejorar la comunicación y el vínculo.' },
    { title: 'Talleres de bienestar', desc: 'Espacios grupales para trabajar el bienestar emocional.' },
  ],
  color:       '#6C63FF',
  logo:        null,
  heroImage:   null,
  contacto:    'hola@estudioraiz.com.ar',
  telefono:    '',
  direccion:   '',
  ubicacion:   'Córdoba, Argentina',
};

// ── Tagline generation ────────────────────────────────────────────────────
// Genera un tagline inteligente basado en nombre + feeling + descripción
const generateTagline = (nombre, feeling, descripcion) => {
  const n = nombre?.trim() || '';
  const d = descripcion?.trim() || '';

  // Extraer una keyword del giro del negocio si está disponible
  const keywordMatch = d.match(/(?:somos|hacemos|ofrecemos|brindamos|ayudamos a|me dedico a|trabajamos en)\s+([^,.]+)/i);
  const keyword = keywordMatch ? keywordMatch[1].trim().toLowerCase() : '';

  const templates = {
    exacto: [
      n ? `${n}: exactamente lo que estabas buscando.` : 'Exactamente lo que estabas buscando.',
      keyword ? `Cuando necesitás ${keyword}, ya lo encontraste.` : 'Llegaste al lugar correcto.',
    ],
    saber: [
      n ? `Hay algo distinto en ${n}.` : 'Algo que vale la pena conocer.',
      keyword ? `Todo sobre ${keyword}, en un solo lugar.` : 'Vale la pena conocer.',
    ],
    confianza: [
      n ? `${n}. El lugar en el que podés confiar.` : 'El lugar en el que podés confiar.',
      keyword ? `Serios con ${keyword}. Cercanos con vos.` : 'Serios y cercanos, siempre.',
    ],
    ahora: [
      n ? `Cuando lo necesitás, ${n} está.` : 'Cuando lo necesitás, estamos.',
      keyword ? `Rápido, real y efectivo en ${keyword}.` : 'Rápido, real y efectivo.',
    ],
  };

  const options = templates[claveImpresion(feeling)] || [
    n ? `${n}. Tecnología que sirve.` : 'Tecnología que sirve.',
  ];

  return options[0];
};

// Tagline hint según lo que el usuario quiere que sienta quien entra a su web
const TAGLINE_HINTS = {
  exacto: 'Ej: La solución exacta para quienes necesitaban algo como esto',
  saber:            'Ej: Algo que vale la pena conocer',
  confianza:      'Ej: El lugar en el que podés confiar',
  ahora:           'Ej: Cuando lo necesitás, estamos',
};

// ── Íconos de línea (viewBox 24, trazo currentColor) ──────────────────────
const ICONOS = {
  negocio:   'M3 10l1.5-5h15L21 10M4 10v10h16V10M3 10h18M9 20v-5h6v5',
  identidad: 'M12 3a9 9 0 100 18c1.4 0 2-1 2-2 0-1.5-1-1.800-1-3 0-1 .8-2 2-2h2.500A3.500 3.500 0 0021 10.500C21 6.400 17 3 12 3zM7.500 11h.01M10 7.500h.01M14.500 7.500h.01',
  servicios: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  contacto:  'M4 5h16v14H4zM4 7l8 6 8-6',
  check:     'M5 12.500l4.500 4.500L19 7.500',
  mas:       'M12 5v14M5 12h14',
  cerrar:    'M6 6l12 12M18 6L6 18',
  imagen:    'M4 5h16v14H4zM4 16l5-5 4 4 3-3 4 4M15.500 9h.01',
};

const Icono = ({ nombre }) => (
  <svg className="wd-icono" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d={ICONOS[nombre]} />
  </svg>
);

// ── Campo con etiqueta real ───────────────────────────────────────────────
const Field = ({ id, label, hint, children }) => (
  <div className="wd-campo">
    <label className="wd-label" htmlFor={id}>{label}</label>
    {hint && <p className="wd-hint" id={`${id}-hint`}>{hint}</p>}
    {children}
  </div>
);

const Input = ({ id, hint, value, onChange, placeholder, multiline, type = 'text', autoComplete }) => {
  const comun = {
    id,
    value,
    placeholder,
    onChange: e => onChange(e.target.value),
    'aria-describedby': hint ? `${id}-hint` : undefined,
  };
  return multiline
    ? <textarea {...comun} rows={4} className="wd-input wd-input--area" />
    : <input {...comun} type={type} autoComplete={autoComplete} className="wd-input" />;
};

// ── Avance ────────────────────────────────────────────────────────────────
const SECTION_KEYS = ['negocio', 'identidad', 'servicios', 'contacto'];

const Avance = ({ completeness }) => {
  const hechas = SECTION_KEYS.filter(k => completeness[k]).length;
  const pct = Math.round((hechas / SECTION_KEYS.length) * 100);
  return (
    <div className="wd-avance">
      <p className="wd-avance__texto">{hechas} de {SECTION_KEYS.length} secciones completas</p>
      <div className="wd-barra" aria-hidden="true">
        <div className="wd-barra__relleno" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ── Color picker ──────────────────────────────────────────────────────────
const PALETTE = [
  ['#29D880', 'Verde menta'], ['#6C63FF', 'Violeta'], ['#FF6B6B', 'Coral'], ['#F59E0B', 'Ámbar'],
  ['#0EA5E9', 'Celeste'], ['#EC4899', 'Rosa'], ['#14B8A6', 'Turquesa'], ['#8B5CF6', 'Lila'],
];

const ColorPicker = ({ value, onChange }) => (
  <div className="wd-colores" role="group" aria-label="Colores sugeridos">
    {PALETTE.map(([c, nombre]) => (
      <button
        key={c}
        type="button"
        onClick={() => onChange(c)}
        className="wd-color"
        aria-label={nombre}
        aria-pressed={value === c}
        style={{ background: c }}
      />
    ))}
    <input
      type="color"
      value={value || '#29D880'}
      onChange={e => onChange(e.target.value)}
      className="wd-color wd-color--libre"
      aria-label="Color personalizado"
    />
  </div>
);

// ── Subida de imágenes ────────────────────────────────────────────────────
const leerImagen = (e, onChange) => {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => onChange(ev.target.result);
  reader.readAsDataURL(file);
};

const LogoUploader = ({ value, onChange }) => {
  const ref = useRef();
  return (
    <div className="wd-subida">
      <button type="button" onClick={() => ref.current.click()} className="wd-logo" aria-label={value ? 'Cambiar logo' : 'Subir logo'}>
        {value
          ? <img src={value} alt="Tu logo" />
          : <Icono nombre="mas" />}
      </button>
      <input ref={ref} type="file" accept="image/*" onChange={e => leerImagen(e, onChange)} hidden />
      {value && (
        <button type="button" onClick={() => onChange(null)} className="wd-texto-boton">
          Quitar logo
        </button>
      )}
    </div>
  );
};

const HeroImageUploader = ({ value, onChange }) => {
  const ref = useRef();
  return (
    <div className="wd-subida wd-subida--col">
      <button type="button" onClick={() => ref.current.click()} className="wd-portada" aria-label={value ? 'Cambiar imagen de portada' : 'Subir imagen de portada'}>
        {value ? (
          <>
            <img src={value} alt="" />
            <span className="wd-portada__etiqueta">Cambiar imagen</span>
          </>
        ) : (
          <>
            <Icono nombre="imagen" />
            <span>Subir imagen</span>
          </>
        )}
      </button>
      <input ref={ref} type="file" accept="image/*" onChange={e => leerImagen(e, onChange)} hidden />
      {value && (
        <button type="button" onClick={() => onChange(null)} className="wd-texto-boton">
          Quitar imagen
        </button>
      )}
    </div>
  );
};

// ── Services list ─────────────────────────────────────────────────────────
const ServicesList = ({ value, onChange }) => {
  const updateField = (i, field, v) => {
    const next = value.map((s, idx) => idx === i ? { ...s, [field]: v } : s);
    onChange(next);
  };
  const add    = () => onChange([...value, { title: '', desc: '' }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="wd-servicios">
      {value.map((s, i) => (
        <div key={i} role="group" aria-label={`Servicio ${i + 1}`} className="wd-servicio">
          <div className="wd-servicio__fila">
            <input
              type="text"
              value={s.title}
              onChange={e => updateField(i, 'title', e.target.value)}
              placeholder="Ej: Terapia individual"
              aria-label={`Nombre del servicio ${i + 1}`}
              className="wd-input"
            />
            <button type="button" onClick={() => remove(i)} className="wd-quitar" aria-label={`Quitar servicio ${i + 1}`}>
              <Icono nombre="cerrar" />
            </button>
          </div>
          <input
            type="text"
            value={s.desc}
            onChange={e => updateField(i, 'desc', e.target.value)}
            placeholder="Descripción breve (opcional)"
            aria-label={`Descripción del servicio ${i + 1}`}
            className="wd-input"
          />
        </div>
      ))}
      <Button variant="solido" onClick={add} className="wd-agregar">
        <Icono nombre="mas" /> Agregar servicio
      </Button>
    </div>
  );
};

// ── Main dashboard ─────────────────────────────────────────────────────────
const Dashboard = () => {
  const { state } = useLocation();
  const navigate  = useNavigate();

  // Si viene del flujo conversacional, usar sus datos como punto de partida
  // chatCollected: datos recolectados por el asistente WebChat
  const fromFlow    = !!(state?.businessInfo || state?.businessName);
  const chat        = state?.chatCollected;         // puede venir de WebChat
  const hasTagline  = !!(chat?.tagline);            // si ya viene del chat, no disparar typewriter
  const taglineHint = TAGLINE_HINTS[claveImpresion(state?.feeling)] || 'Ej: Un espacio para volver a vos.';
  const rubroHint   = fromFlow ? 'Ej: Arquitectura · Psicología · Gastronomía · Indumentaria' : 'Una o dos palabras. Aparece como categoría en la web.';

  const INITIAL = fromFlow ? {
    ...DEMO,
    nombre:      state.businessName || '',
    rubro:       chat?.rubro        || state.rubro || '',
    tagline:     chat?.tagline      || '',
    descripcion: state.businessInfo || '',
    ubicacion:   chat?.ubicacion    || '',
    contacto:    chat?.contacto     || '',
    telefono:    '',
    direccion:   '',
    color:       '',
    heroImage:   null,
    servicios:   chat?.servicios?.length > 0 ? chat.servicios : [{ title: '', desc: '' }],
  } : DEMO;

  const [data, setData] = useState(INITIAL);
  const [section, setSection] = useState('negocio');
  const [showPreview, setShowPreview] = useState(false);
  const [taglineTyping, setTaglineTyping] = useState(false);
  const taglineTimerRef = useRef(null);

  // Al montar: generar tagline con typewriter — solo si no viene ya del chat
  useEffect(() => {
    if (!fromFlow || hasTagline) return;
    const feeling = state?.feeling;
    const nombre  = state?.businessName || '';
    const desc    = state?.businessInfo || '';
    if (!feeling && !nombre && !desc) return;

    const generated = generateTagline(nombre, feeling, desc);
    if (!generated) return;

    // Pequeño delay antes de arrancar — deja que el usuario procese la pantalla
    const startDelay = setTimeout(() => {
      setTaglineTyping(true);
      let i = 0;
      taglineTimerRef.current = setInterval(() => {
        i++;
        setData(d => ({ ...d, tagline: generated.slice(0, i) }));
        if (i >= generated.length) {
          clearInterval(taglineTimerRef.current);
          setTaglineTyping(false);
        }
      }, 45);
    }, 900);

    return () => {
      clearTimeout(startDelay);
      clearInterval(taglineTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (val) => setData(d => ({ ...d, [key]: val }));

  const completeness = {
    negocio:   !!(data.nombre && data.rubro && data.descripcion),
    identidad: !!(data.color && (data.logo || data.heroImage)),
    servicios: data.servicios.filter(s => s.title?.trim()).length > 0,
    contacto:  !!(data.contacto || data.telefono || data.direccion),
  };

  const ids = {
    nombre: 'wd-nombre', rubro: 'wd-rubro', tagline: 'wd-tagline', descripcion: 'wd-descripcion', ubicacion: 'wd-ubicacion',
    logo: 'wd-logo-g', portada: 'wd-portada-g', color: 'wd-color-g', servicios: 'wd-servicios-g',
    contacto: 'wd-contacto', telefono: 'wd-telefono', direccion: 'wd-direccion',
  };

  return (
    <div className="gh-root wd-root" data-theme="claro">
      <FondoMosaico pantalla="web-tablero" />
      <header className="gh-header">
        <a href="/" className="gh-logo" aria-label="GUIA, ir al inicio"><Brujula sobreClaro /></a>
        <div className="gh-header__lado">
          <span className="gh-pildora">
            <button type="button" className="gh-link wd-reiniciar" onClick={() => navigate('/')}>Empezar de nuevo</button>
          </span>
        </div>
      </header>

      <main id="contenido" className="gh-escena wd-escena">

        {/* Cambio de vista: solo en pantallas angostas */}
        <div className="wd-vistas" role="group" aria-label="Qué querés ver">
          <button type="button" className="gh-opcion" aria-pressed={!showPreview} onClick={() => setShowPreview(false)}>Formulario</button>
          <button type="button" className="gh-opcion" aria-pressed={showPreview} onClick={() => setShowPreview(true)}>Vista previa</button>
        </div>

        <div className="wd-grid">

          {/* ── Editor ── */}
          <div className="gh-panel wd-panel wd-editor" data-theme="claro" data-oculto={showPreview}>
            <div className="wd-cabecera">
              <p className="gw-etiqueta">{state?.businessName ? `Web para ${state.businessName}` : 'Tu web'}</p>
              <h1 className="wd-titulo">Armá tu web</h1>
              <p className="wd-lead">
                {fromFlow ? 'Completá los datos y mirá cómo queda.' : 'Completá los datos de tu negocio y mirá cómo queda.'}
              </p>
            </div>

            <Avance completeness={completeness} />

            <nav aria-label="Secciones del formulario" className="wd-pasos">
              <ul>
                {SECTIONS.map(s => {
                  const { icon, label } = SECTION_LABELS[s];
                  const done = completeness[s];
                  return (
                    <li key={s}>
                      <button
                        type="button"
                        className="wd-paso"
                        aria-current={section === s ? 'step' : undefined}
                        onClick={() => setSection(s)}
                      >
                        <Icono nombre={icon} />
                        <span>{label}</span>
                        {done && (
                          <>
                            <Icono nombre="check" />
                            <span className="gh-sr">, completa</span>
                          </>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <section className="wd-seccion" aria-labelledby="wd-seccion-t">
              <h2 className="wd-h2" id="wd-seccion-t">{SECTION_LABELS[section].label}</h2>

              {section === 'negocio' && (
                <>
                  <Field id={ids.nombre} label="Nombre del negocio">
                    <Input id={ids.nombre} value={data.nombre} onChange={set('nombre')} placeholder="Ej: Estudio Raíz" autoComplete="organization" />
                  </Field>
                  <Field id={ids.rubro} label="Rubro" hint={rubroHint}>
                    <Input id={ids.rubro} hint={rubroHint} value={data.rubro} onChange={set('rubro')} placeholder="Ej: Arquitectura" />
                  </Field>
                  <Field
                    id={ids.tagline}
                    label={
                      <>
                        Frase principal
                        {taglineTyping && <span className="wd-escribiendo"> · Proponiendo una frase…</span>}
                      </>
                    }
                    hint={fromFlow && state?.feeling
                      ? `Querés que sientan: ${sinComillas(state.feeling)}. Editala como quieras.`
                      : 'La frase grande que se ve primero en tu web.'
                    }
                  >
                    <div className="wd-escritura">
                      <Input
                        id={ids.tagline}
                        hint
                        value={data.tagline}
                        onChange={val => { clearInterval(taglineTimerRef.current); setTaglineTyping(false); set('tagline')(val); }}
                        placeholder={taglineHint}
                      />
                      {taglineTyping && <span className="wd-cursor" aria-hidden="true" />}
                    </div>
                  </Field>
                  <Field id={ids.descripcion} label="Descripción" hint="Dos o tres oraciones: qué hacés y para quién.">
                    <Input id={ids.descripcion} hint multiline value={data.descripcion} onChange={set('descripcion')} placeholder="Qué hacés y para quién..." />
                  </Field>
                  <Field id={ids.ubicacion} label="Ubicación">
                    <Input id={ids.ubicacion} value={data.ubicacion} onChange={set('ubicacion')} placeholder="Ej: Córdoba, Argentina" />
                  </Field>
                </>
              )}

              {section === 'identidad' && (
                <>
                  <Field id={ids.logo} label="Logo" hint="Se ve arriba, en el menú de tu web.">
                    <LogoUploader value={data.logo} onChange={set('logo')} />
                  </Field>
                  <Field id={ids.portada} label="Imagen de portada" hint="Foto del local, del equipo o del producto. Se ve de fondo en la primera pantalla.">
                    <HeroImageUploader value={data.heroImage} onChange={set('heroImage')} />
                  </Field>
                  <Field id={ids.color} label="Color principal" hint="El color que distingue a tu web.">
                    <ColorPicker value={data.color} onChange={set('color')} />
                  </Field>
                </>
              )}

              {section === 'servicios' && (
                <Field id={ids.servicios} label="Tus servicios" hint="Un servicio por tarjeta. Los más importantes, primero.">
                  <ServicesList value={data.servicios} onChange={set('servicios')} />
                </Field>
              )}

              {section === 'contacto' && (
                <>
                  <Field id={ids.contacto} label="Email de contacto" hint="El que aparece en tu web para que te escriban.">
                    <Input id={ids.contacto} hint type="email" autoComplete="email" value={data.contacto} onChange={set('contacto')} placeholder="hola@tunegocio.com.ar" />
                  </Field>
                  <Field id={ids.telefono} label="Teléfono o WhatsApp" hint="Opcional. Se muestra en tu web si lo completás.">
                    <Input id={ids.telefono} hint type="tel" autoComplete="tel" value={data.telefono} onChange={set('telefono')} placeholder="Ej: +54 9 351 123-4567" />
                  </Field>
                  <Field id={ids.direccion} label="Dirección" hint="Opcional. Útil si tenés un local o consultorio.">
                    <Input id={ids.direccion} hint autoComplete="street-address" value={data.direccion} onChange={set('direccion')} placeholder="Ej: Av. Hipólito Yrigoyen 1234, Córdoba" />
                  </Field>
                </>
              )}
            </section>
          </div>

          {/* ── Vista previa ── */}
          <section className="gh-panel wd-panel wd-previa" data-fase="charla" data-oculto={!showPreview} aria-labelledby="wd-previa-t">
            <h2 className="wd-h2" id="wd-previa-t">Vista previa</h2>
            <p className="wd-nota-previa">Así va quedando tu web. Se actualiza mientras escribís.</p>
            {/* Es una maqueta que acompaña al formulario: no se navega ni se lee aparte, por eso va inerte. */}
            <div className="wd-marco" inert="" aria-hidden="true">
              <WebPreview data={data} />
            </div>
          </section>

        </div>
      </main>

      <footer className="gh-pie">
        <span>Córdoba, Argentina</span>
        <Link to="/privacidad">Privacidad</Link>
      </footer>
    </div>
  );
};

export default Dashboard;
